import { useMemo, useState } from "react"
import { toast } from "sonner"
import { postInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"
import { dishBasePrice, dishUnitPrice, requiredGroupsSatisfied } from "./dish-pricing"
import { parseDecimal } from "./money"
import { usePickupCart } from "./pickup-cart"
import type { MenuItem, OrderServiceType } from "./types"
import { t } from "@/lib/i18n"

/**
 * Shared order state for the dish detail (drawer + deep-link page).
 *
 * Where the dish *goes* is the one thing that differs between the two contexts,
 * and it is decided here rather than by the five components above this hook. A
 * seated diner adds to a bill that already exists, so the dish POSTs to
 * `/orders`. Someone ordering for pickup has no bill and must not get one:
 * an order that exists is an order the kitchen cooks, so the dish goes into
 * localStorage and reaches the server only when they pay.
 */
export function useDishOrder(
  item: MenuItem,
  initialServiceType: OrderServiceType = "eat_in",
  options?: {
    /**
     * The pre-seating menu: browsing only. `submit` becomes a no-op — belt
     * and suspenders, since the caller that would render the button that
     * calls it is already gone in this mode. Nothing on screen should offer a
     * control that would 303, and this is the one place left that could.
     */
    readOnly?: boolean
  }
) {
  const readOnly = options?.readOnly ?? false
  const pickupCart = usePickupCart()
  const groups = useMemo(() => item.extraGroups ?? [], [item.extraGroups])
  const [qty, setQty] = useState(1)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [note, setNote] = useState("")
  const [serviceType, setServiceType] = useState<OrderServiceType>(initialServiceType)
  const [submitting, setSubmitting] = useState(false)

  // A promoted base only on the rail that will honour it.
  //
  // `Location::OrderFactory` runs the promotion evaluation inside the
  // transaction that creates a dine-in line, so a rule quoted here is a rule the
  // ticket charges. The pickup rail runs none: `Location::PickupMaterializationService`
  // creates its orders directly, and `User::PickupCartValidationService` re-reads
  // every price off the item before the charge. Putting a promoted figure into a
  // pickup cart would fail that re-read as a *price change* and refuse the
  // checkout the diner is standing at.
  //
  // The server already declines to send `promotion` on a pickup menu. This is
  // the same invariant said locally, where the cart write actually happens, so
  // one caller passing the wrong resolver cannot turn into a refused payment.
  const promotedItem = pickupCart ? { ...item, promotion: null } : item
  const base = dishBasePrice(promotedItem)

  const basePrice = useMemo(() => parseDecimal(base), [base])
  const originalPrice = useMemo(() => parseDecimal(item.price), [item.price])
  const unitPrice = useMemo(
    () => dishUnitPrice(base, groups, selectedIds),
    [base, groups, selectedIds]
  )
  const total = unitPrice * qty
  const canSubmit = useMemo(
    () => requiredGroupsSatisfied(groups, selectedIds),
    [groups, selectedIds]
  )

  // Checkbox groups (maxSelect !== 1) toggle freely; a group capped at one
  // selection behaves like a radio: picking an extra clears its siblings.
  const toggleExtra = (groupId: number, extraId: number) => {
    setSelectedIds((previous) => {
      const group = groups.find((candidate) => candidate.id === groupId)
      const extra = group?.extras.find((candidate) => candidate.id === extraId)
      if (!group || !extra || extra.status === "sold_out") return previous

      const next = new Set(previous)
      const wasSelected = next.has(extraId)

      if (group.maxSelect === 1) {
        for (const groupExtra of group.extras) next.delete(groupExtra.id)
        if (!wasSelected) next.add(extraId)
        return next
      }

      if (wasSelected) {
        next.delete(extraId)
        return next
      }

      // Refuse selections past maxSelect so the group can't reach an
      // over-cap state that silently disables add-to-cart.
      const selectedCount = group.extras.filter((candidate) => next.has(candidate.id)).length
      if (group.maxSelect != null && selectedCount >= group.maxSelect) return previous

      next.add(extraId)
      return next
    })
  }

  // Lets a radio-style group with minSelect === 0 return to "no selection".
  const clearGroup = (groupId: number) => {
    setSelectedIds((previous) => {
      const group = groups.find((candidate) => candidate.id === groupId)
      if (!group) return previous

      const next = new Set(previous)
      for (const extra of group.extras) next.delete(extra.id)
      return next
    })
  }

  // The server bounces back to the current screen, so the toast is the only
  // confirmation the diner gets — they stay on the menu to keep ordering.
  const submit = (submitOptions?: { onSuccess?: () => void }) => {
    if (readOnly || submitting || !canSubmit) return

    // Pickup: nothing leaves the browser. Same toast, same "stay on the menu"
    // behaviour, so the two contexts feel identical to the diner even though
    // one of them has not written a thing.
    if (pickupCart) {
      pickupCart.add(item, {
        quantity: qty,
        extraIds: Array.from(selectedIds),
        notes: note.trim() || null,
      })
      toast.success(t("ui.react.features.user.pickup_cart.added"))
      submitOptions?.onSuccess?.()
      return
    }

    setSubmitting(true)
    postInline(
      "/dinein/orders",
      {
        order: {
          item_id: item.id,
          quantity: qty,
          notes: note.trim() || null,
          extra_ids: Array.from(selectedIds),
          service_type: serviceType,
        },
      },
      { preserveState: true }
    )
      .then((result) => {
        toastInlineUpdateSuccess(result)
        submitOptions?.onSuccess?.()
      })
      .catch(toastInlineUpdateError)
      .finally(() => setSubmitting(false))
  }

  return {
    /**
     * True on a pickup screen, where the dish goes into localStorage. The dish
     * components read it to know that "no open bill" is not a reason to refuse
     * the add — a pickup cart has no bill by design.
     */
    usesLocalCart: Boolean(pickupCart),
    qty,
    setQty,
    groups,
    selectedIds,
    toggleExtra,
    clearGroup,
    note,
    setNote,
    serviceType,
    setServiceType,
    submitting,
    basePrice,
    /** The menu price. Equal to `basePrice` unless a rule reduced it. */
    originalPrice,
    /** The rule behind the reduction, or null. Null on every pickup screen. */
    promotion: promotedItem.promotion ?? null,
    unitPrice,
    total,
    canSubmit,
    submit,
  }
}
